import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Send, Mail, Linkedin, Github, Twitter, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setStatus({ type: "success", message: response.data.message || "Message sent successfully!" });
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.detail || "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const socialLinks = [
    { icon: Mail, href: "mailto:loveloop9055@gmail.com", label: "Email" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
    { icon: Github, href: "#", label: "GitHub" },
    { icon: Twitter, href: "#", label: "Twitter" },
  ];

  return (
    <section
      id="contact"
      ref={ref}
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0C0C0C]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">
              Get In Touch
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
              Let's Work Together
            </h2>
            <p className="text-[#888888] max-w-xl mx-auto">
              Have a project in mind? I'd love to hear about it. Send me a
              message and let's create something amazing together.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Contact Form */}
            <motion.div variants={itemVariants}>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label
                    htmlFor="name"
                    className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-2 block"
                  >
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-transparent border-b border-white/20 px-0 py-4 text-white focus:border-[#D4FF00] transition-colors w-full outline-none"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-2 block"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-transparent border-b border-white/20 px-0 py-4 text-white focus:border-[#D4FF00] transition-colors w-full outline-none"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-2 block"
                  >
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-transparent border-b border-white/20 px-0 py-4 text-white focus:border-[#D4FF00] transition-colors w-full outline-none resize-none"
                    placeholder="Tell me about your project..."
                  />
                </div>

                {/* Status Message */}
                {status.type && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex items-center gap-2 p-4 border ${
                      status.type === "success"
                        ? "border-[#00E676]/30 bg-[#00E676]/10 text-[#00E676]"
                        : "border-[#FF3B30]/30 bg-[#FF3B30]/10 text-[#FF3B30]"
                    }`}
                  >
                    {status.type === "success" ? (
                      <CheckCircle size={18} />
                    ) : (
                      <AlertCircle size={18} />
                    )}
                    <span className="text-sm">{status.message}</span>
                  </motion.div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="bg-[#D4FF00] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors duration-300 btn-press inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info & Social Links */}
            <motion.div variants={itemVariants} className="space-y-12">
              {/* Direct Contact */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Direct Contact</h3>
                <a
                  href="mailto:loveloop9055@gmail.com"
                  data-testid="contact-email-link"
                  className="text-[#888888] hover:text-[#D4FF00] transition-colors text-lg"
                >
                  loveloop9055@gmail.com
                </a>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">Follow Me</h3>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={social.label}
                      href={social.href}
                      data-testid={`social-${social.label.toLowerCase()}`}
                      aria-label={social.label}
                      className="w-12 h-12 border border-white/20 flex items-center justify-center text-[#888888] hover:border-[#D4FF00] hover:text-[#D4FF00] hover:bg-[#D4FF00]/10 transition-all duration-300"
                    >
                      <social.icon size={20} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="bg-[#050505] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-2 h-2 bg-[#00E676] rounded-full animate-pulse" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[#888888]">
                    Currently Available
                  </span>
                </div>
                <p className="text-white font-medium">
                  Open for freelance projects and collaborations
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
